import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const ATTRIBUTE_SEARCH_RESOURCE = "app-role-attribute" as const;
export const ATTRIBUTE_SEARCH_OPERATION = "search" as const;
export const ATTRIBUTE_SEARCH_API_PATH = `/v1/${ATTRIBUTE_SEARCH_RESOURCE}/${ATTRIBUTE_SEARCH_OPERATION}` as const;

export type AttributeSearchParameters = paths[typeof ATTRIBUTE_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type AttributeSearchBody =
  paths[typeof ATTRIBUTE_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type AttributeSearchResponse =
  paths[typeof ATTRIBUTE_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useAttributeSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<AttributeSearchParameters, AttributeSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<AttributeSearchResponse, Error, AttributeSearchResponse, readonly unknown[]>;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<AttributeSearchParameters, AttributeSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      ATTRIBUTE_SEARCH_RESOURCE,
      ATTRIBUTE_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<AttributeSearchResponse>(
        ATTRIBUTE_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
